<script>
    import Info from 'lucide-svelte/icons/info';
    import X from 'lucide-svelte/icons/x';
    import { onDestroy, onMount } from 'svelte';
    import tippy from 'tippy.js';

    let isTouchDevice = false;
    let showModal = false;
    let tippyInstance = null;
    let formattedContent = [];

    // ===============================
    // COMPONENT PROPS
    // ===============================
    export let heading = 'A heading';
    export let link = 'More info';
    export let content = `This is some explanatory text that provides more details about the topic.
    You can add more content here, and it will be scrollable in the modal on touch devices.`;

    onMount(() => {
        formattedContent =
            typeof content === 'string'
                ? content.split('\r').map((p) => p.trim())
                : Array.isArray(content)
                  ? content
                  : [];
        isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    });

    onDestroy(() => {
        if (tippyInstance) {
            tippyInstance.destroy();
        }
    });

    const handleEscapeKey = (e) => {
        if (e.key === 'Escape') {
            handleTap();
        }
    };

    const handleMouseEnter = (event) => {
        if (!isTouchDevice) {
            if (!tippyInstance) {
                tippyInstance = tippy(event.target, {
                    content: `
                    <h2>${heading}</h2>
                    <p>${formattedContent.join('</p><p>')}</p>
                    `,
                    arrow: true,
                    animation: 'fade',
                    allowHTML: true,
                });
            }
            tippyInstance.show();
        }
    };

    const handleMouseLeave = () => {
        if (tippyInstance) tippyInstance.hide();
    };

    const handleTap = () => {
        showModal = !showModal;
        if (showModal) {
            window.addEventListener('keydown', handleEscapeKey);
        } else {
            window.removeEventListener('keydown', handleEscapeKey);
        }
    };
</script>

<!-- Trigger -->
<span
    class="link"
    role="button"
    tabindex="0"
    on:mouseenter={handleMouseEnter}
    on:mouseleave={handleMouseLeave}
    on:click={handleTap}
    on:keydown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            handleTap();
        }
    }}
>
    <Info />
    {link}
</span>

<!-- Modal for touch screens -->
{#if isTouchDevice}
    <div class="modal-container {showModal ? 'visible' : ''}">
        <!-- Modal backdrop -->
        <div
            class="backdrop"
            role="button"
            tabindex="0"
            aria-label="Close Modal"
            on:click={handleTap}
            on:keydown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    handleTap();
                }
            }}
        ></div>

        <!-- Modal window -->
        <div class="modal">
            <div class="modal-header">
                <h2>{heading}</h2>
                <span
                    class="modal-close"
                    role="button"
                    tabindex="0"
                    aria-label="Close modal"
                    on:click={handleTap}
                    on:keydown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            handleTap();
                        }
                    }}><X /></span
                >
            </div>
            <div class="modal-content">
                {#each formattedContent as p}
                    <p>{@html p}</p>
                {/each}
            </div>
        </div>
    </div>
{/if}

<style lang="scss">
    .link {
        color: #06aee0;
        cursor: help;
        font-size: var(--scale-2);
        text-decoration: none;
        white-space: nowrap;
    }

    .link:hover {
        text-decoration: underline;
    }

    .modal-container {
        position: fixed;
        z-index: 999;
        display: none;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        inset: 0;
        white-space: normal !important;
    }

    .modal-container.visible {
        display: flex;
    }

    .modal {
        position: relative;
        z-index: 999;
        display: flex;
        width: 90%;
        height: 66vh;
        flex-direction: column;
        padding: var(--spacing0);
        margin: 0 var(--spacing0);
        background: var(--color-text-body);
        box-shadow: 0 0 5px rgb(0 0 0 / 50%);
        color: #fff;
        font-family: 'Tiempos Text', 'Georgia Adjusted', georgia, serif !important;
        font-size: var(--scale0);
        overflow-y: auto;
    }

    .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0;
        font-size: 24px !important;
        font-weight: bold;

        h2 {
            font-family: 'Tiempos Text', 'Georgia Adjusted', georgia, serif !important;
        }
    }

    .modal-content {
        p {
            margin-top: 0 !important;
            line-height: 26px !important;
        }
    }

    .modal-close {
        cursor: pointer;
        font-weight: bold;
    }

    .backdrop {
        position: fixed;
        z-index: 998;
        background-color: rgb(0 0 0 / 33%);
        inset: 0;
    }
</style>
